import React, { useState, useMemo } from 'react';
import { MapPin, Route } from 'lucide-react';
import ScrollableClubCardModal from './ScrollableClubCardModal';
import { Carousel, CarouselContent, CarouselItem } from './carousel';
import { getAllImageUrls, convertHeicToWebFormat, getDisplayImageUrl, getImageLoadingProps } from '../../utils/imageUtils';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceFromUser } from '../../utils/distanceUtils';

// Helper to clean IDs (remove any appended indices like :1)
const cleanId = (id) => {
  if (!id) return null;
  const idStr = String(id);
  // Remove any colon and everything after it (e.g., "id:1" -> "id")
  const cleaned = idStr.split(':')[0].trim();
  return cleaned || null;
};

const ClubCard = ({ 
  club, 
  onClick, 
  showHover = true,
  showType = true,
  showConnections = true,
  showLocation = true,
  showNextRun = true,
  showRating = true,
  buttonText = "Connect",
  onButtonClick,
  /** When true, use loading="eager" and fetchpriority="high" for the primary card image (e.g. visible carousel slide). */
  priority = false,
  /** When true, use card-sized image URLs for list/homepage (faster load). */
  useCardSize = false,
  // Preview functionality props
  isPreview = false,
  onImagePositionChange,
  isImagePositioning = false,
  onClubUpdate,
  onConnectionChange // Callback when connect/disconnect happens
}) => {
  const { user } = useAuth();
  const displayUnit = user?.distance_unit || 'miles';
  const [showClubModal, setShowClubModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  /** When true, card variant 404ed; use full-size URLs (backward compat for existing images). */
  const [cardUrlFailed, setCardUrlFailed] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Clean the club ID to remove any appended indices
  const cleanedClubId = useMemo(() => cleanId(club?.id), [club?.id]);

  // Get image URLs from runcard_images or clubcard_images (mobile parity)
  const imageUrls = useMemo(() => {
    const images = club?.runcard_images || club?.clubcard_images;
    if (images && Array.isArray(images) && images.length > 0) {
      return getAllImageUrls(images);
    }
    if (club?.runcard_image) {
      return [convertHeicToWebFormat(club.runcard_image)];
    }
    return [];
  }, [club?.runcard_images, club?.clubcard_images, club?.runcard_image]);

  const useCard = useCardSize && !cardUrlFailed;
  const displayUrls = useMemo(
    () => imageUrls.map((url) => getDisplayImageUrl(url, useCard)),
    [imageUrls, useCard]
  );
  const primaryDisplayUrl = displayUrls.length > 0 ? displayUrls[0] : null;

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (onButtonClick) {
      onButtonClick(club);
    } else {
      // Open club modal if no custom button handler
      setShowClubModal(true);
    }
  };

  // Image positioning handlers for preview mode
  const handleImageMouseDown = (e) => {
    if (!isPreview || !isImagePositioning) return;
    e.preventDefault();
    // This would be handled by the parent component
  };

  const handleImageMouseMove = (e) => {
    if (!isPreview || !isImagePositioning) return;
    e.preventDefault();
    // This would be handled by the parent component
  };

  const handleImageMouseUp = (e) => {
    if (!isPreview || !isImagePositioning) return;
    e.preventDefault();
    // This would be handled by the parent component
  };

  const handleClick = () => {
    if (onClick) {
      onClick(club);
    }
  };

  return (
    <div
      className={`rounded-3xl overflow-hidden relative bg-runcards-background ${
        showHover ? 'hover:scale-105 transition-transform duration-300' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
      onMouseDown={isPreview ? onImagePositionChange?.onMouseDown : undefined}
      onMouseMove={isPreview ? onImagePositionChange?.onMouseMove : undefined}
      onMouseUp={isPreview ? onImagePositionChange?.onMouseUp : undefined}
      onMouseLeave={isPreview ? onImagePositionChange?.onMouseUp : undefined}
      style={{ 
        cursor: isPreview && isImagePositioning ? (onImagePositionChange?.isDragging ? 'grabbing' : 'grab') : (onClick ? 'pointer' : 'default')
      }}
    >
      {/* Image Section - Carousel when multiple images, single image otherwise */}
      <div className="relative h-[260px] sm:h-[300px] overflow-hidden rounded-t-3xl rounded-b-3xl">
        {imageUrls.length > 1 ? (
          <div
            className="w-full h-full relative"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
          >
            <Carousel
              opts={{ align: "start", loop: true }}
              className="w-full h-full"
              setApi={(api) => {
                if (api) {
                  setCarouselApi(api);
                  setCurrentImageIndex(api.selectedScrollSnap());
                  api.on('select', () => setCurrentImageIndex(api.selectedScrollSnap()));
                }
              }}
            >
              <CarouselContent className="h-full -ml-0">
                {displayUrls.map((url, index) => (
                  <CarouselItem key={index} className="h-full pl-0 basis-full">
                    <div className="relative w-full h-full">
                      <img
                        src={url}
                        alt={`${club.name} - Image ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        {...getImageLoadingProps(priority && index === 0)}
                        style={{
                          objectPosition: club.imagePosition ? `${club.imagePosition.x}% ${club.imagePosition.y}%` : 'center center'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleButtonClick(e);
                        }}
                        onError={(e) => {
                          if (useCardSize && e.target.src.includes('_card.jpg')) {
                            setCardUrlFailed(true);
                            return;
                          }
                          setImageError(true);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => setImageError(false)}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div role="group" aria-label="Image carousel" onClick={(e) => e.stopPropagation()} className="absolute inset-0 pointer-events-none">
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-auto">
                  {imageUrls.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Image ${index + 1} of ${imageUrls.length}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        carouselApi?.scrollTo(index);
                      }}
                      className={`rounded-full transition-all duration-200 ${
                        currentImageIndex === index
                          ? 'w-2 h-2 bg-white shadow'
                          : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </Carousel>
          </div>
        ) : primaryDisplayUrl && !imageError ? (
          <img
            src={primaryDisplayUrl}
            alt={club.name}
            className="w-full h-full object-cover cursor-pointer"
            {...getImageLoadingProps(priority)}
            style={{
              objectPosition: club.imagePosition ? `${club.imagePosition.x}% ${club.imagePosition.y}%` : 'center center'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick(e);
            }}
            onError={(e) => {
              if (useCardSize && e.target.src.includes('_card.jpg')) {
                setCardUrlFailed(true);
                return;
              }
              setImageError(true);
              e.target.style.display = 'none';
            }}
            onLoad={() => setImageError(false)}
          />
        ) : null}
        <div className={`w-full h-full bg-runcards-background spray-paint-placeholder`} style={{ display: (imageUrls.length > 1 || primaryDisplayUrl) && !imageError ? 'none' : 'flex' }}>
        </div>
      </div>

      {/* Information Section - Dark background like mobile */}
      {!isImagePositioning && (
        <div className="bg-runcards-background rounded-b-3xl">
          {/* Basic Info Section */}
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-runcards-text-primary font-runcards-bold">
                {club.name}
              </h3>
            </div>
            
            {/* Club Details - mobile parity: city • type • skill_level, then distance */}
            <div className="flex flex-col space-y-2 mb-4">
              {(showLocation && club.city) || (showType && club.type) || club.skill_level ? (
                <div className="flex flex-wrap items-center gap-x-1 text-sm text-runcards-text-primary">
                  {showLocation && club.city && (
                    <>
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1.5 text-runcards-text-secondary flex-shrink-0" />
                        {club.city}
                      </span>
                      {(showType && club.type) || club.skill_level ? <span className="text-runcards-text-secondary">•</span> : null}
                    </>
                  )}
                  {showType && club.type && (
                    <>
                      <span>{club.type}</span>
                      {club.skill_level ? <span className="text-runcards-text-secondary">•</span> : null}
                    </>
                  )}
                  {club.skill_level && (
                    <span>{club.skill_level.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}</span>
                  )}
                </div>
              ) : null}
              {(club.distanceFromUser != null || club.distance_from_user != null) && (
                <div className="flex items-center text-runcards-text-primary">
                  <Route className="w-4 h-4 mr-2 text-runcards-text-secondary flex-shrink-0" />
                  <span className="text-sm">
                    {formatDistanceFromUser(Number(club.distanceFromUser ?? club.distance_from_user), displayUnit)} away
                  </span>
                </div>
              )}
              {showNextRun && club.nextRun && (
                <div className="flex items-center text-runcards-text-primary">
                  <span className="text-sm">
                    Next: {club.nextRun}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Simple instruction when positioning */}
      {isPreview && isImagePositioning && (
        <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-blue-300 text-sm text-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
            🖱️ Click and drag the image above to position it
          </p>
        </div>
      )}

      {/* Club Card Modal */}
      <ScrollableClubCardModal
        clubId={cleanedClubId}
        isOpen={showClubModal}
        onClose={() => setShowClubModal(false)}
        onConnectionChange={onConnectionChange}
      />
    </div>
  );
};

export default ClubCard;
